/* SpawnAnything — battle statistics (feature:stats), counted inside the sim worker.
   Pure bookkeeping on typed arrays: the worker calls these hooks, nothing here writes simulation state,
   uses the PRNG or enters the desync hash. Damage sources: the worker sets a context (source + attacker)
   before every path that can deal damage; damage()/bDamage() then book against it.
   Also imported by the client (layout constants + decode), so no worker globals here. */

// damage sources (array offsets, not shared ids)
export const SRC_WEAPON = 0, SRC_BUILDING = 1, SRC_TOOL = 2, SRC_STOMP = 3, SRC_FALL = 4, NSRC = 5;
export const HG = 64;              // heatmap grid (HG × HG over [-W, W]²)
export const SAMPLE_DT = 0.5;      // army strength sample interval (sim seconds), doubles when the timeline is full
const TLCAP = 2048;                // timeline samples kept (2 teams each); halved by decimation when full
const SEND_DT = 0.5;               // client update interval (sim seconds)

// packet layout (Float32Array): header, then timeline (2 × ns), then heat (2 × HG²)
export const H = {
  T: 0, END: 1, DT: 2, NS: 3, W: 4, EXPL: 5,
  BEST: 6,       // idx, kills, team, alive
  LONG: 10,      // per team: idx, life, alive, kills
  BLOST: 18, DEAD: 20, ALIVE: 22, START: 24,
  DMG: 26,       // [team * NSRC + src] damage received by team (units + buildings)
  KILL: 36,      // [team * NSRC + src] units of team killed by src
  SIZE: 46,
};

export function createStats() {
  return {
    on: false, N: 0, W: 1, t: 0, end: -1, dt: SAMPLE_DT, nextS: 0, lastSent: -1, dirty: false,
    src: 0, by: -1, expl: 0,
    alive: null, team: null,                  // read-only references to the sim's ALIVE / TEAM
    kills: new Uint16Array(0), life: new Uint16Array(0), birth: new Float32Array(0),
    pBy: new Int32Array(0), pSrc: new Uint8Array(0), pLife: new Uint16Array(0),
    dmg: new Float64Array(2 * NSRC), kill: new Uint32Array(2 * NSRC),
    dead: new Uint32Array(2), blost: new Uint32Array(2), start: new Uint32Array(2), cur: new Uint32Array(2),
    best: -1, bestK: 0, bestT: 0, bestLife: 0,
    longDead: new Int32Array([-1, -1]), longDeadL: new Float64Array(2),
    tl: new Float32Array(TLCAP * 2), ns: 0,
    heat: new Uint32Array(2 * HG * HG),
  };
}

/** New battle: size the per-unit arrays (reused when large enough), clear all counters. */
export function statsInit(S, N, W, pmax, alive, team, n0, n1) {
  S.on = true; S.N = N; S.W = W > 0 ? W : 1; S.t = 0; S.end = -1; S.dt = SAMPLE_DT; S.nextS = 0; S.lastSent = -1; S.dirty = false;
  S.src = 0; S.by = -1; S.expl = 0; S.alive = alive; S.team = team;
  if (S.kills.length < N) { S.kills = new Uint16Array(N); S.life = new Uint16Array(N); S.birth = new Float32Array(N); }
  else { S.kills.fill(0); S.life.fill(0); S.birth.fill(0); }
  if (S.pBy.length < pmax) { S.pBy = new Int32Array(pmax); S.pSrc = new Uint8Array(pmax); S.pLife = new Uint16Array(pmax); }
  S.dmg.fill(0); S.kill.fill(0); S.dead.fill(0); S.blost.fill(0); S.heat.fill(0);
  S.start[0] = n0; S.start[1] = n1; S.cur[0] = n0; S.cur[1] = n1;
  S.best = -1; S.bestK = 0; S.bestT = 0; S.bestLife = 0;
  S.longDead[0] = S.longDead[1] = -1; S.longDeadL[0] = S.longDeadL[1] = 0;
  S.ns = 0;
}

/** Start of a tick: advance the clock, sample army strength; freezes everything once the battle is over. */
export function statsTick(S, dt, a0, a1, over) {
  if (!S.on) return;
  S.cur[0] = a0; S.cur[1] = a1;
  if (S.t >= S.nextS || over) {
    if (S.ns >= TLCAP) { decimate(S); }
    S.tl[S.ns * 2] = a0; S.tl[S.ns * 2 + 1] = a1; S.ns++;
    S.nextS += S.dt; S.dirty = true;
  }
  if (over) { S.on = false; S.end = S.t; S.dirty = true; return; }
  S.t += dt;
}
// timeline full: keep every second sample, sample half as often from now on
function decimate(S) {
  const h = S.ns >> 1;
  for (let k = 0; k < h; k++) { S.tl[k * 2] = S.tl[k * 4]; S.tl[k * 2 + 1] = S.tl[k * 4 + 1]; }
  S.ns = h; S.dt *= 2; S.nextS = h * S.dt;
}

/** Damage context: who deals the damage booked next (src = SRC_*, by = unit index, -2-b building, -1 none). */
export function statsCtx(S, src, by) { S.src = src; S.by = by; }
/** A projectile was fired in the current context: remember it for the impact. */
export function statsProj(S, p) { S.pSrc[p] = S.src; S.pBy[p] = S.by; S.pLife[p] = S.by >= 0 ? S.life[S.by] : 0; }
/** A projectile moves / lands: its shooter is the context (dropped when the slot now holds another unit). */
export function statsCtxProj(S, p) {
  S.src = S.pSrc[p]; const by = S.pBy[p];
  S.by = by >= 0 && S.life[by] !== S.pLife[p] ? -1 : by;
}
export function statsExpl(S) { if (S.on) { S.expl++; S.dirty = true; } }
/** God tool dropped (granate, fass, atom, ...): context + one explosion. */
export function statsBomb(S) { S.src = SRC_TOOL; S.by = -1; statsExpl(S); }

/** Unit j of team t took dmg; hp = its HP after the hit (<= 0: it died at (x, z)). */
export function statsHit(S, j, t, dmg, hp, x, z) {
  if (!S.on) { if (j === S.best && hp <= 0) S.dirty = true; return; }
  const src = S.src, o = t * NSRC + src;
  S.dmg[o] += hp < 0 ? dmg + hp : dmg;
  if (hp > 0) return;
  S.kill[o]++; S.dead[t]++; S.dirty = true;
  const by = S.by;
  if (by >= 0 && (src === SRC_WEAPON || src === SRC_STOMP)) {
    const k = S.kills[by] < 65535 ? ++S.kills[by] : 65535;
    if (k > S.bestK) { S.bestK = k; S.best = by; S.bestT = S.team[by]; S.bestLife = S.life[by]; }
  }
  const life = S.t - S.birth[j];
  if (S.longDead[t] < 0 || life > S.longDeadL[t]) { S.longDead[t] = j; S.longDeadL[t] = life; }
  const W = S.W, f = HG / (2 * W);
  let gx = ((x + W) * f) | 0, gz = ((z + W) * f) | 0;
  if (gx < 0) gx = 0; else if (gx >= HG) gx = HG - 1;
  if (gz < 0) gz = 0; else if (gz >= HG) gz = HG - 1;
  S.heat[t * HG * HG + gz * HG + gx]++;
}
/** Building of team t took dmg (after armor); hp = its HP after the hit. */
export function statsBHit(S, t, dmg, hp) {
  if (!S.on) return;
  S.dmg[t * NSRC + S.src] += hp < 0 ? dmg + hp : dmg;
  if (hp <= 0) { S.blost[t]++; S.dirty = true; }
}
/** A building spawned unit i: new life in a reused slot. */
export function statsSpawn(S, i) {
  S.life[i] = (S.life[i] + 1) & 65535; S.kills[i] = 0; S.birth[i] = S.t;
  if (i === S.best) S.dirty = true;
}

// longest-living unit of team t: alive since the earliest birth (ties: most kills, lowest index), else the last death
function longest(S, t, out, o) {
  const A = S.alive, T = S.team;
  let bi = -1, bb = 0, bk = -1;
  for (let i = 0; i < S.N; i++) {
    if (!A[i] || T[i] !== t) continue;
    const b = S.birth[i], k = S.kills[i];
    if (bi < 0 || b < bb || (b === bb && k > bk)) { bi = i; bb = b; bk = k; }
  }
  if (bi >= 0) { out[o] = bi; out[o + 1] = S.t - bb; out[o + 2] = 1; out[o + 3] = bk; return; }
  const d = S.longDead[t];
  out[o] = d; out[o + 1] = d >= 0 ? S.longDeadL[t] : 0; out[o + 2] = 0; out[o + 3] = d >= 0 ? S.kills[d] : 0;
}

/** Packet for the client: every SEND_DT of sim time while something changed (and once more at the end), else null. */
export function statsFrame(S) {
  if (!S.dirty || !S.N) return null;
  if (S.on && S.lastSent >= 0 && S.t - S.lastSent < SEND_DT) return null;
  S.dirty = false; S.lastSent = S.t;
  return encodeStats(S);
}
export function encodeStats(S) {
  const ns = S.ns, b = new Float32Array(H.SIZE + ns * 2 + 2 * HG * HG);
  b[H.T] = S.t; b[H.END] = S.end; b[H.DT] = S.dt; b[H.NS] = ns; b[H.W] = S.W; b[H.EXPL] = S.expl;
  const bi = S.best;
  b[H.BEST] = bi; b[H.BEST + 1] = S.bestK; b[H.BEST + 2] = S.bestT;
  b[H.BEST + 3] = bi >= 0 && S.alive[bi] && S.life[bi] === S.bestLife ? 1 : 0;
  longest(S, 0, b, H.LONG); longest(S, 1, b, H.LONG + 4);
  for (let t = 0; t < 2; t++) { b[H.BLOST + t] = S.blost[t]; b[H.DEAD + t] = S.dead[t]; b[H.ALIVE + t] = S.cur[t]; b[H.START + t] = S.start[t]; }
  for (let k = 0; k < 2 * NSRC; k++) { b[H.DMG + k] = S.dmg[k]; b[H.KILL + k] = S.kill[k]; }
  b.set(S.tl.subarray(0, ns * 2), H.SIZE);
  const o = H.SIZE + ns * 2;
  for (let k = 0; k < 2 * HG * HG; k++) b[o + k] = S.heat[k];
  return b;
}

/** Packet -> plain object (client and tests). */
export function decodeStats(b) {
  const ns = b[H.NS] | 0, o = H.SIZE + ns * 2;
  const team = t => ({
    dead: b[H.DEAD + t], alive: b[H.ALIVE + t], start: b[H.START + t], blost: b[H.BLOST + t],
    dmg: Array.from(b.subarray(H.DMG + t * NSRC, H.DMG + (t + 1) * NSRC)),
    kill: Array.from(b.subarray(H.KILL + t * NSRC, H.KILL + (t + 1) * NSRC)),
    longest: { i: b[H.LONG + t * 4], life: b[H.LONG + t * 4 + 1], alive: !!b[H.LONG + t * 4 + 2], kills: b[H.LONG + t * 4 + 3] },
  });
  return {
    t: b[H.T], end: b[H.END], dt: b[H.DT], ns, W: b[H.W], expl: b[H.EXPL],
    best: { i: b[H.BEST], kills: b[H.BEST + 1], team: b[H.BEST + 2], alive: !!b[H.BEST + 3] },
    teams: [team(0), team(1)],
    tl: b.subarray(H.SIZE, o), heat: b.subarray(o, o + 2 * HG * HG),
  };
}
